"use client"

import { useMemo } from "react"
import { Team, User } from "@/schema"
import { TaskTemplate } from "@/schema/task-template"
import { ComboboxInput } from "@ui/forms/inputs/combobox-input"
import { Button, TooltipProvider } from "@ui/shad"
import { ListChecks } from "lucide-react"

import { useFetchCompanyTaskTemplates } from "@/hooks/api/company-task-templates"
import { useFetchTeams } from "@/hooks/api/teams"
import { useFetchUsers } from "@/hooks/api/users"

export interface TaskTemplateAssignment {
  assignedUser: User | null
  assignedTeam: Team | null
  assignedToCustomFieldId: string | null
}

interface TaskTemplatePickerProps {
  onApply: (template: TaskTemplate, assignment: TaskTemplateAssignment) => void
}

export function TaskTemplatePicker({ onApply }: TaskTemplatePickerProps) {
  const { data, isLoading } = useFetchCompanyTaskTemplates()
  const { data: usersData } = useFetchUsers()
  const { data: teamsData } = useFetchTeams()

  const taskTemplates = useMemo(() => data?.task_templates ?? [], [data?.task_templates])
  const users = useMemo(() => usersData?.users ?? [], [usersData?.users])
  const teams = useMemo(() => teamsData?.teams ?? [], [teamsData?.teams])

  const options = useMemo(() => {
    return taskTemplates.map((template) => ({
      value: template.id,
      label: template.title?.markdown?.trim() || "Untitled template",
    }))
  }, [taskTemplates])

  const handleSelect = (value: string) => {
    const template = taskTemplates.find((t) => t.id === value)
    if (template) {
      if (template.assigned_to_custom_field_id) {
        onApply(template, {
          assignedUser: null,
          assignedTeam: null,
          assignedToCustomFieldId: template.assigned_to_custom_field_id,
        })
        return
      }

      if (template.assigned_team_id) {
        onApply(template, {
          assignedUser: null,
          assignedTeam: teams.find((team) => team.id === template.assigned_team_id) || null,
          assignedToCustomFieldId: null,
        })
        return
      }

      onApply(template, {
        assignedUser: template.assigned_to_id
          ? users.find((user) => user.id === template.assigned_to_id) || null
          : null,
        assignedTeam: null,
        assignedToCustomFieldId: null,
      })
    }
  }

  const triggerButton = (
    <Button size="iconXs" variant="ghost" type="button" aria-label="Apply task template" tooltip="Apply task template">
      <ListChecks className="text-icon h-4 w-4" />
    </Button>
  )

  if (taskTemplates.length === 0 && !isLoading) {
    return null
  }

  return (
    <TooltipProvider>
      <ComboboxInput
        id="task-template-picker"
        value=""
        onChange={handleSelect}
        options={options}
        placeholder="Search templates..."
        emptyText="No templates found"
        triggerButton={triggerButton}
        contentProps={{ align: "start", side: "bottom" }}
        renderOption={(option) => <span className="truncate">{option.label}</span>}
      />
    </TooltipProvider>
  )
}
